import React, { useState } from 'react';
import { useOmniAgentPick } from '../../context/OmniAgentContext';
import './DiffCard.css';

// The resume's sections, rearranged by the assistant (chat-2, 2026-10-30):
// a proposal from the arrange_resume_sections tool, decided here the way a
// resume edit is (DiffCard.js): the changes in plain lines, the order before
// and after on request, and Apply or Discard. Apply writes exactly what the
// Resume editor writes when the person drags, hides or renames a section
// (ProfileContainer.js savePreviewSetting: PUT /api/profile/sectionSettings
// { data }), through OmniAgentContext.acceptArrangement. Discard writes
// nothing; the assistant is told, so it does not offer the same order again.

const fallbackName = (id) => String(id || '').charAt(0) + String(id || '').slice(1).toLowerCase();

/** A section's name in one arrangement: its own title when it has one, else the resume's label. */
export const sectionName = (id, state, labels) => state?.titles?.[id] || labels?.[id] || fallbackName(id);

function Order({ title, state, labels }) {
  const order = Array.isArray(state?.order) ? state.order : [];
  const hidden = new Set(Array.isArray(state?.hidden) ? state.hidden : []);
  const hiddenOnly = [...hidden].filter(id => !order.includes(id));
  return (
    <div className="ag-arr-col">
      <span className="ag-arr-col-name">{title}</span>
      <ol className="ag-arr-order">
        {order.map(id => (
          <li key={id} className={hidden.has(id) ? 'is-hidden' : undefined}>
            {sectionName(id, state, labels)}
            {hidden.has(id) && <span className="ag-arr-tag">hidden</span>}
          </li>
        ))}
      </ol>
      {hiddenOnly.length > 0 && (
        <p className="ag-arr-hidden">Hidden: {hiddenOnly.map(id => sectionName(id, state, labels)).join(', ')}</p>
      )}
    </div>
  );
}

export default function ArrangeCard({ proposal }) {
  const { acceptArrangement, rejectArrangement, arrangeOutcomes = {} } = useOmniAgentPick('acceptArrangement', 'rejectArrangement', 'arrangeOutcomes');
  const [phase, setPhase] = useState('idle'); // idle | applying | failed
  const [showOrder, setShowOrder] = useState(false);
  if (!proposal) return null;
  const outcome = arrangeOutcomes[proposal.proposalId];
  const changes = Array.isArray(proposal.changes) ? proposal.changes.filter(Boolean) : [];
  const canCompare = Boolean(proposal.before?.order || proposal.after?.order);

  const apply = async () => {
    setPhase('applying');
    const ok = await acceptArrangement(proposal);
    setPhase(ok ? 'idle' : 'failed');
  };

  return (
    <div className="ag-cc ag-arr" role="group" aria-label="Proposed section changes">
      <header className="ag-cc-head">
        <div className="ag-cc-title">
          <span className="ag-cc-section">Resume</span>
          <span className="ag-cc-name">Sections</span>
        </div>
      </header>

      {changes.length > 0
        ? <ul className="ag-arr-changes">{changes.map((c, i) => <li key={i}>{c}</li>)}</ul>
        : <p className="ag-cc-empty">No changes here.</p>}

      {canCompare && (
        <div className="ag-arr-compare">
          <button type="button" className="ag-cc-more" aria-expanded={showOrder} onClick={() => setShowOrder(o => !o)}>
            <span className="material-symbols-rounded" aria-hidden="true">{showOrder ? 'expand_less' : 'expand_more'}</span>
            {showOrder ? 'Hide the order' : 'Show the order'}
          </button>
          {showOrder && (
            <div className="ag-arr-cols">
              <Order title="Before" state={proposal.before} labels={proposal.labels} />
              <Order title="After" state={proposal.after} labels={proposal.labels} />
            </div>
          )}
        </div>
      )}

      {outcome ? (
        <div className="ag-cc-foot">
          <div className={`ag-cc-status ag-arr-status${outcome === 'applied' ? ' is-done' : ''}`} role="status">
            <span className="material-symbols-rounded" aria-hidden="true">{outcome === 'applied' ? 'check' : 'close'}</span>
            {outcome === 'applied' ? 'Applied to your resume' : 'Discarded'}
          </div>
        </div>
      ) : (
        <div className="ag-cc-foot">
          {phase === 'failed' ? (
            <span className="ag-cc-status ag-arr-status is-error" role="alert">
              <span className="material-symbols-rounded" aria-hidden="true">error</span>
              Couldn&apos;t save. Nothing changed.
            </span>
          ) : <span className="ag-cc-count" />}
          <div className="ag-cc-actions">
            <button type="button" className="ag-cc-btn is-quiet" disabled={phase === 'applying'} onClick={() => rejectArrangement(proposal)}>Discard</button>
            <button type="button" className="ag-cc-btn is-primary" disabled={phase === 'applying'} onClick={apply}>
              {phase === 'applying' ? 'Applying…' : phase === 'failed' ? 'Try again' : 'Apply'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
